/* eslint-disable camelcase */
import { useState, useEffect } from 'react';
import { Auth } from 'aws-amplify';
import jwt_decode from 'jwt-decode';

/**
 * Roles that grant user-management ownership of a project. Kept in sync with the
 * backend `USER_MANAGEMENT_ROLES` (userManagementAuthorization.ts): both the
 * dedicated `userManagement` role and a project `superadmin` can manage users.
 */
const USER_MANAGEMENT_ROLES = ['userManagement', 'superadmin'];

/**
 * Literal (non project-prefixed) Cognito group that grants GLOBAL user-management.
 * A member can manage Cognito users across ALL projects, analogous to `rootAccess`
 * but restricted to user management. Must match the backend literal group string EXACTLY.
 */
const GLOBAL_USER_MANAGEMENT_GROUP = 'userManagementAll';

/**
 * Derive the set of projects where the user holds a user-management-granting role
 * (`<project>:userManagement` or `<project>:superadmin`).
 * Malformed group strings are ignored so they never grant ownership.
 * @param {Array<string>} groups - Cognito groups (e.g. `projectA:userManagement`, `rootAccess`).
 * @returns {Array<string>} Unique list of owned project permissions.
 */
function deriveOwnedProjects(groups) {
  if (!Array.isArray(groups)) return [];
  const owned = new Set();
  groups.forEach((group) => {
    if (typeof group !== 'string') return;
    const parts = group.split(':');
    if (parts.length === 2 && parts[0] && USER_MANAGEMENT_ROLES.includes(parts[1])) {
      owned.add(parts[0]);
    }
  });
  return Array.from(owned);
}

function useRole() {
  const [role, setRole] = useState(null);
  const [groups, setGroups] = useState([]);
  const [isRoot, setIsRoot] = useState(false);
  const [isGlobalUserManager, setIsGlobalUserManager] = useState(false);
  const [ownedProjects, setOwnedProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function fetchRole() {
      try {
        const user = await Auth.currentAuthenticatedUser();
        const decodedToken = jwt_decode(user.signInUserSession.accessToken.jwtToken);
        if (decodedToken) {
          const rawGroups = decodedToken['cognito:groups'] ? decodedToken['cognito:groups'] : decodedToken.groups;
          setGroups(rawGroups || []);
          setIsRoot(Array.isArray(rawGroups) && rawGroups.includes('rootAccess'));
          setIsGlobalUserManager(Array.isArray(rawGroups) && rawGroups.includes(GLOBAL_USER_MANAGEMENT_GROUP));
          setOwnedProjects(deriveOwnedProjects(rawGroups));
          if (rawGroups[0] === 'rootAccess') {
            setRole('rootAccess');
            return;
          }
          setRole(rawGroups[0].split(':')[1]);
        }
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    }

    fetchRole();
  }, []);

  return {
    role, groups, isRoot, isGlobalUserManager, ownedProjects, loading, error,
  };
}

export default useRole;
